import { createNgModuleRef, Injectable, Injector, Type } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';

import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  combineLatest,
  EMPTY,
  from,
  Observable,
  of as observableOf,
  Subject,
  switchMap,
  take,
} from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  first,
  map,
  share,
  shareReplay,
  skip,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { AccountSelectors } from '@recruitee/account-data';
import { filterBoolean } from '@recruitee/common';
import { VisitedEntitiesHistoryService } from '@recruitee/dashboard-logic';
import { CandidateVisitedSnapshot } from '@recruitee/dashboard-types';
import { I18nService } from '@recruitee/i18n';
import { MailboxCache } from '@recruitee/mailbox-data';
import {
  DialogService,
  ProtectedControlsDialogComponent,
  RtPlatform,
} from '@recruitee/user-interface';
import { macroTask } from '@recruitee/utils';

import { PageTitleService } from '../common/services/page-title.service';
import { selectCandidateData } from '../features/candidate/store/feature';
import { candidateResolver } from './resolvers/candidate.resolver';

type CandidateDialogData =
  import('../features/candidate/candidate-dialog/candidate-dialog.component').CandidateDialogData;
type CandidateDialogComponent =
  import('../features/candidate/candidate-dialog/candidate-dialog.component').CandidateDialogComponent;

@Injectable({ providedIn: 'root' })
export class CandidateDialogService {
  private ngOnDestroy$ = new Subject<void>();

  private isOpen$ = new Subject<boolean>();
  private candidateData$ = new BehaviorSubject<CandidateDialogData>(<any>null);
  private candidateDialogComponent$ = from(import('../features/candidate/candidate.module')).pipe(
    map(({ CandidateDialogComponent, RtCandidateFeatureModule }) => ({
      CandidateDialogComponent,
      candidateFeatureModuleRef: createNgModuleRef(RtCandidateFeatureModule, this.injector),
    })),
    shareReplay(),
  );

  constructor(
    private dialogService: DialogService,
    private platform: RtPlatform,
    private mailboxCache: MailboxCache,
    private route: ActivatedRoute,
    private store: Store,
    private router: Router,
    private pageTitleService: PageTitleService,
    private i18nService: I18nService,
    private injector: Injector,
    private visitedEntitiesHistoryService: VisitedEntitiesHistoryService<CandidateVisitedSnapshot>,
  ) {
    this.candidateDialogComponent$.pipe(take(1)).subscribe();
  }

  // eslint-disable-next-line max-lines-per-function
  public init(): void {
    this.isOpen$
      .pipe(
        distinctUntilChanged(),
        switchMap(isOpen =>
          this.candidateDialogComponent$.pipe(
            switchMap(({ CandidateDialogComponent, candidateFeatureModuleRef }) =>
              isOpen
                ? this.openDialog(
                    CandidateDialogComponent,
                    candidateFeatureModuleRef.injector,
                    this.candidateData$,
                  )
                : EMPTY,
            ),
          ),
        ),
      )
      .subscribe();

    combineLatest([
      this.store.select(selectCandidateData),
      this.isOpen$,
      this.router.events.pipe(filter(event => event instanceof NavigationEnd)),
    ])
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(([candidate, isOpen]) => {
        if (!candidate || !isOpen) return;

        const profileString = this.i18nService.translate('app.candidate.profile');
        const pageTitle = `${candidate.name} - ${profileString}`;
        this.pageTitleService.setCustomTitle(pageTitle);
      });

    combineLatest([
      this.route.queryParams.pipe(filterBoolean),
      this.store.select(AccountSelectors.currentCompany).pipe(filterBoolean, first()),
    ])
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(([queryParams]) =>
        queryParams.candidate ? this.open(queryParams) : this.close(),
      );
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public open(queryParams: Params): void {
    this.candidateData$.next({
      candidateId: +queryParams.candidate,
      tab: queryParams.tab ? queryParams.tab : undefined,
      offerId: queryParams.offerId ? queryParams.offerId : undefined,
      openInterview: queryParams.openInterview ? true : undefined,
      interviewOfferId: queryParams.interviewOfferId ? +queryParams.interviewOfferId : undefined,
      interviewTalentPoolId: queryParams.interviewTalentPoolId
        ? +queryParams.interviewTalentPoolId
        : undefined,
      interviewTemplateId: queryParams.interviewTemplateId
        ? +queryParams.interviewTemplateId
        : undefined,
      interviewEdit: queryParams.interviewEdit ? !!queryParams.interviewEdit : undefined,
      interviewResultId: queryParams.interviewResultId ? +queryParams.interviewResultId : undefined,
      interviewResultRequestId: queryParams.interviewResultRequestId
        ? +queryParams.interviewResultRequestId
        : undefined,
      fromHiddenEvaluations: queryParams.fromHiddenEvaluations
        ? !!queryParams.fromHiddenEvaluations
        : undefined,
    });
    this.isOpen$.next(true);

    candidateResolver(this.store, selectCandidateData)
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(candidate => this.visitedEntitiesHistoryService.updateHistory(candidate));
  }

  public close(): void {
    this.isOpen$.next(false);
  }

  // eslint-disable-next-line max-lines-per-function
  private openDialog(
    component: Type<CandidateDialogComponent>,
    injector: Injector,
    data$: BehaviorSubject<CandidateDialogData>,
  ) {
    // eslint-disable-next-line max-lines-per-function
    return new Observable(observer => {
      const destroy$ = new Subject<void>();
      const data = data$.value;
      const dialogRef = this.dialogService.openCustom(
        component,
        {
          showCloseButton: false,
          disableClose: false,
          containerOptions: {
            hasPadding: false,
          },
          injector,
          closeGuard: () => {
            const { protectedControls } = dialogRef.componentInstance;
            if (protectedControls.locked) {
              return this.dialogService
                .openCustom(ProtectedControlsDialogComponent, {}, {})
                .afterClosed.pipe(
                  tap(result => {
                    if (result) {
                      this.mailboxCache.remove(
                        this.mailboxCache.key('candidate', data.candidateId),
                      );
                    }
                  }),
                  share(),
                );
            } else {
              return observableOf(true);
            }
          },
        },
        this.getInitialData(data$.value),
      );

      data$.pipe(skip(1), takeUntil(destroy$)).subscribe(data => {
        dialogRef.componentInstance.setDialogData(data, true);
      });
      dialogRef.afterClosed.pipe(take(1)).subscribe(() => {
        macroTask(() => this.clearUrlParams());
        this.isOpen$.next(false);
        observer.complete();
      });
      return () => {
        destroy$.next();
        destroy$.complete();
        dialogRef.close();
      };
    });
  }

  private getInitialData<T extends CandidateDialogData>(data: T): CandidateDialogData {
    return {
      candidateId: data.candidateId,
      offerId: data.offerId,
      tab: data.tab,
      openInterview: data.openInterview,
      interviewOfferId: data.interviewOfferId,
      interviewTalentPoolId: data.interviewTalentPoolId,
      interviewTemplateId: data.interviewTemplateId,
      interviewEdit: data.interviewEdit,
      interviewResultId: data.interviewResultId,
      interviewResultRequestId: data.interviewResultRequestId,
      fromHiddenEvaluations: data.fromHiddenEvaluations,
    };
  }

  private clearUrlParams() {
    this.router.navigate([], {
      queryParams: {
        candidate: null,
        offerId: null,
        tab: null,
        messagesMode: null,
        smsThread: null,
        openInterview: null,
        interviewOfferId: null,
        interviewTalentPoolId: null,
        interviewTemplateId: null,
        interviewEdit: null,
        interviewResultId: null,
        interviewResultRequestId: null,
        fromHiddenEvaluations: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: false,
    });
  }
}
